import { useSupabaseLoader } from "../../../../../../server/supabase/hooks";

import type { Json } from "../../../../../../__definitions__/type-definitions";
import type { UseSupabaseLoaderState } from "../../../../../../server/supabase/hooks/types";
import type { UUID } from "../../../../../../utils/uuid";
import type { GetPersonsInSegmentJsonPerson } from "../../../../types/segment-schema";

export type GetPersonsArgs =
  | {
      segment_id_: UUID;
      target_connection_type_?: string;
      target_manager_?: boolean;
    }
  | {
      segment_json: Json;
      target_connection_type_?: string;
      target_manager_?: boolean;
    };

export function usePersonsInSegmentJson(
  args: GetPersonsArgs | null,
): UseSupabaseLoaderState<GetPersonsInSegmentJsonPerson[]> {
  return useSupabaseLoader(
    async ({ supabase, account_id }) => {
      if (!args) return [];

      const allPersons: GetPersonsInSegmentJsonPerson[] = [];
      let lastEmail: string | undefined = undefined;
      let keepFetching = true;
      const PAGE_SIZE = 10_000;

      while (keepFetching) {
        const allArgs = {
          account_id_: account_id,
          ...args,
        };

        let query = supabase
          .rpc("get_persons_in_segment_json", allArgs)
          .order("email", { ascending: true })
          .limit(PAGE_SIZE);

        if (lastEmail) {
          query = query.gt("email", lastEmail);
        }

        const { data: batch, error: err } = await query;

        if (err) throw err;

        if (batch && batch.length > 0) {
          allPersons.push(...batch);

          lastEmail = batch[batch.length - 1]?.email;

          if (batch.length < PAGE_SIZE || !lastEmail) {
            keepFetching = false;
          }
        } else {
          // No data returned at all
          keepFetching = false;
        }
      }

      return allPersons;
    },
    [args],
  );
}
